/**
 * Web Push subscription helpers.
 *
 *  isPushSupported()                  — environment check
 *  loadPushStatus()                   — fetch /api/push/status (configured + subscribed)
 *  subscribeToPush()                  — request permission, register, POST to backend
 *  unsubscribeFromPush()              — drop subscription locally + server-side
 *  sendTestPush()                     — POST /api/push/test
 */
import { api } from "@/lib/api";

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export async function loadPushStatus() {
  try {
    const r = await api.get("/push/status");
    return r.data;
  } catch {
    return { configured: false, subscribed: false };
  }
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function arrayBufferToBase64(buf) {
  const bytes = new Uint8Array(buf);
  let s = "";
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return window.btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export async function subscribeToPush() {
  if (!isPushSupported()) throw new Error("Push isn't supported on this device");
  const { configured, public_key } = await loadPushStatus();
  if (!configured || !public_key) throw new Error("Push isn't configured on the server");
  // Permission gate — the browser will only honour it from a user gesture
  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error("Notifications were not allowed");
  const reg = await navigator.serviceWorker.ready;
  // If an old subscription exists with a different key, drop it first
  const existing = await reg.pushManager.getSubscription();
  if (existing) await existing.unsubscribe().catch(() => {});
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(public_key),
  });
  const json = sub.toJSON();
  await api.post("/push/subscribe", {
    endpoint: sub.endpoint,
    p256dh: json.keys?.p256dh || arrayBufferToBase64(sub.getKey("p256dh")),
    auth: json.keys?.auth || arrayBufferToBase64(sub.getKey("auth")),
    ua: navigator.userAgent.slice(0, 200),
  });
  return sub;
}

export async function unsubscribeFromPush() {
  if (!isPushSupported()) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  const endpoint = sub.endpoint;
  await sub.unsubscribe().catch(() => {});
  try { await api.post("/push/unsubscribe", { endpoint }); } catch { /* ignore */ }
}

export async function sendTestPush() {
  const r = await api.post("/push/test", {});
  return r.data;
}
